import React from "react";
import { Box } from "@mui/material";

const formatCurrency = (value, currency = "AFN") => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return currency === "AFN" ? "0 ؋" : `0 ${currency}`;
  if (currency === "AFN") return `${amount.toLocaleString()} ؋`;
  return `${amount.toLocaleString()} ${currency}`;
};

const labelize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const HISTORY_COLUMN_ORDER = [
  "id",
  "date",
  "patient",
  "doctor",
  "fees",
  "due",
  "amount",
  "total_price",
  "total_amount",
  "discount",
  "status",
  "payment_status",
  "visit_date",
  "visit_time",
  "currency",
  "description",
];

const getHistoryColumns = (history = []) => {
  if (!history.length) return [];
  const sample = history[0];
  return HISTORY_COLUMN_ORDER.filter((key) => sample[key] !== undefined);
};

const getPersonLabel = (value) => {
  if (!value || typeof value !== "object") return "-";
  const name = value.name || value.username || "-";
  const id = value.id != null ? `ID: ${value.id}` : "";
  return id ? `${name} (${id})` : name;
};

const formatHistoryValue = (column, value) => {
  if (value == null || value === "") return "-";
  if (column === "id") return `#${value}`;
  if (column === "patient" || column === "doctor") return getPersonLabel(value);
  if (column === "date") return new Date(value).toLocaleString();
  if (column.endsWith("_date")) return new Date(value).toLocaleDateString();
  if (column.endsWith("_time")) {
    const time = new Date(`1970-01-01T${value}`);
    return Number.isNaN(time.getTime()) ? value : time.toLocaleTimeString();
  }
  if (
    column === "fees" ||
    column === "due" ||
    column === "amount" ||
    column === "total_price" ||
    column === "total_amount" ||
    column === "discount"
  ) {
    return formatCurrency(value);
  }
  return String(value);
};

const getHistoryTotalMeta = (history = []) => {
  const totalField = ["fees", "amount", "total_price", "total_amount"].find((field) =>
    history.some((row) => row[field] !== undefined)
  );
  if (!totalField) return null;
  const total = history.reduce((sum, row) => sum + (Number(row[totalField] || 0) || 0), 0);
  const label = totalField === "fees" ? "Total Fees" : "Total Amount";
  return { label, total };
};

const buildSummaryHistory = (group, item) => {
  if (group === "special") {
    return [
      {
        id: "-",
        description: item.label,
        amount: item.total?.AFN || 0,
        currency: Object.keys(item.total || {}).join(", ") || "AFN",
      },
    ];
  }

  return [
    {
      id: "-",
      description: item.label,
      amount: item.total || 0,
      status: item.count > 0 ? `${item.count} Transaction(s)` : "No Transactions",
    },
  ];
};

const printStyles = `
  @page {
    size: A4 portrait;
    margin: 12mm;
  }

  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: #16313d;
    font-family: Arial, Helvetica, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .finance-print-root {
    width: 100%;
    padding: 0;
    box-sizing: border-box;
  }

  .finance-print-shell {
    border: 1px solid #d6e1e7;
    border-radius: 14px;
    overflow: hidden;
  }

  .finance-print-header {
    padding: 18px 22px 14px;
    background: linear-gradient(135deg, #0f5f78 0%, #1c809a 100%);
    color: #ffffff;
  }

  .finance-print-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .finance-print-title {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .finance-print-subtitle {
    margin: 6px 0 0;
    font-size: 13px;
    opacity: 0.92;
  }

  .finance-print-stamp {
    text-align: right;
    font-size: 12px;
    line-height: 1.5;
    min-width: 180px;
  }

  .finance-print-body {
    padding: 18px 22px 20px;
  }

  .finance-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 18px;
  }

  .finance-summary-card {
    border-radius: 12px;
    padding: 14px 16px;
    color: #ffffff;
    page-break-inside: avoid;
  }

  .finance-summary-card.revenue {
    background: linear-gradient(135deg, #1b7a43 0%, #249e57 100%);
  }

  .finance-summary-card.expenses {
    background: linear-gradient(135deg, #9f2f2c 0%, #c74642 100%);
  }

  .finance-summary-card.net {
    background: linear-gradient(135deg, #0f5f78 0%, #1b88a6 100%);
  }

  .finance-summary-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.9;
  }

  .finance-summary-value {
    margin-top: 10px;
    font-size: 25px;
    font-weight: 700;
    line-height: 1.2;
  }

  .finance-summary-extra {
    margin-top: 4px;
    font-size: 12px;
    opacity: 0.95;
  }

  .finance-section-content {
    padding: 10px 14px 14px;
  }

  .finance-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .finance-table th,
  .finance-table td {
    padding: 8px 6px;
    border-bottom: 1px solid #e6edf1;
    text-align: left;
    vertical-align: top;
  }

  .finance-table th {
    color: #5d6e78;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .finance-table tr:last-child td {
    border-bottom: none;
  }

  .finance-footer {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #d7e4ea;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 12px;
    color: #5d6e78;
  }

  .finance-history-stack {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .finance-history-card {
    border: 1px solid #d7e4ea;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    page-break-inside: avoid;
  }

  .finance-history-header {
    padding: 11px 14px;
    background: #0f5f78;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .finance-history-footer {
    padding: 10px 14px;
    border-top: 1px solid #d7e4ea;
    background: #f7fbfc;
    display: flex;
    justify-content: flex-end;
    font-size: 12px;
    font-weight: 700;
    color: #16313d;
  }

  @media screen {
    .finance-print-root {
      display: none;
    }
  }

  @media print {
    .finance-print-root {
      display: block !important;
    }
  }
`;

const SummaryCard = ({ tone, label, values = [] }) => (
  <div className={`finance-summary-card ${tone}`}>
    <div className="finance-summary-label">{label}</div>
    {values.map((value, index) => (
      <div
        key={`${label}-${index}`}
        className={index === 0 ? "finance-summary-value" : "finance-summary-extra"}
      >
        {value}
      </div>
    ))}
  </div>
);

const HistoryTable = ({ title, history }) => {
  const columns = getHistoryColumns(history);
  const totalMeta = getHistoryTotalMeta(history);

  if (!history?.length || !columns.length) return null;

  return (
    <div className="finance-history-card">
      <div className="finance-history-header">{title}</div>
      <div className="finance-section-content">
        <table className="finance-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={`${title}-${column}`}>{labelize(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map((row, index) => (
              <tr key={`${title}-row-${index}`}>
                {columns.map((column) => (
                  <td key={`${title}-row-${index}-${column}`}>
                    {formatHistoryValue(column, row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalMeta ? (
        <div className="finance-history-footer">
          {totalMeta.label}: {formatCurrency(totalMeta.total)}
        </div>
      ) : null}
    </div>
  );
};

const FinancialReportPrint = ({
  data,
  summaryRevenue = [],
  summaryExpenses = [],
  summaryNetIncome = [],
}) => {
  const medicalCards = Object.entries(data?.medical_services || {}).map(([key, service]) => ({
    key,
    label: labelize(key),
    count: Number(service?.count || 0),
    total: Number(service?.total || 0),
  }));

  const specialCards = Object.entries(data?.special_payments || {}).map(([key, service]) => ({
    key,
    label: labelize(key),
    count: Number(service?.count || 0),
    total: service?.total || {},
  }));

  const expenseCards = Object.entries(data?.expenses || {}).map(([key, service]) => ({
    key,
    label: labelize(key),
    count: Number(service?.count || 0),
    total: Number(service?.total || 0),
  }));

  const historyTables = [
    ...medicalCards.map((item) => ({
      key: item.key,
      group: "medical",
      title: `${item.label} ${Array.isArray(data?.medical_services?.[item.key]?.history) && data.medical_services[item.key].history.length ? "History" : "Summary"}`,
      history:
        Array.isArray(data?.medical_services?.[item.key]?.history) &&
        data.medical_services[item.key].history.length
          ? data.medical_services[item.key].history
          : buildSummaryHistory("medical", item),
    })),
    ...specialCards.map((item) => ({
      key: item.key,
      group: "special",
      title: `${item.label} ${Array.isArray(data?.special_payments?.[item.key]?.history) && data.special_payments[item.key].history.length ? "History" : "Summary"}`,
      history:
        Array.isArray(data?.special_payments?.[item.key]?.history) &&
        data.special_payments[item.key].history.length
          ? data.special_payments[item.key].history
          : buildSummaryHistory("special", item),
    })),
    ...expenseCards.map((item) => ({
      key: item.key,
      group: "expense",
      title: `${item.label} ${Array.isArray(data?.expenses?.[item.key]?.history) && data.expenses[item.key].history.length ? "History" : "Summary"}`,
      history:
        Array.isArray(data?.expenses?.[item.key]?.history) &&
        data.expenses[item.key].history.length
          ? data.expenses[item.key].history
          : buildSummaryHistory("expense", item),
    })),
  ]
    .sort((a, b) => {
      if (a.key === "appointments") return -1;
      if (b.key === "appointments") return 1;
      if (a.group !== b.group) {
        const order = { medical: 1, special: 2, expense: 3 };
        return (order[a.group] || 99) - (order[b.group] || 99);
      }
      return a.title.localeCompare(b.title);
    });

  const reportRange =
    data?.date_range?.start && data?.date_range?.end
      ? `${new Date(data.date_range.start).toLocaleDateString()} - ${new Date(
          data.date_range.end
        ).toLocaleDateString()}`
      : "All Time";

  return (
    <Box className="finance-print-root">
      <style>{printStyles}</style>
      <div className="finance-print-shell">
        <div className="finance-print-header">
          <div className="finance-print-header-top">
            <div>
              <h1 className="finance-print-title">Financial Dashboard Report</h1>
              <p className="finance-print-subtitle">
                Hospital Plus financial summary and service collections
              </p>
            </div>
            <div className="finance-print-stamp">
              <div>
                <strong>Period:</strong> {reportRange}
              </div>
              <div>
                <strong>Generated:</strong> {new Date().toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div className="finance-print-body">
          <div className="finance-summary-grid">
            <SummaryCard
              tone="revenue"
              label="Total Revenue"
              values={summaryRevenue.map((item) => formatCurrency(item.value, item.currency))}
            />
            <SummaryCard
              tone="expenses"
              label="Total Expenses"
              values={summaryExpenses.map((item) => formatCurrency(item.value, item.currency))}
            />
            <SummaryCard
              tone="net"
              label="Net Income"
              values={summaryNetIncome.map((item) => formatCurrency(item.value, item.currency))}
            />
          </div>

          {historyTables.length ? (
            <div className="finance-history-stack">
              {historyTables.map((table) => (
                <HistoryTable
                  key={table.title}
                  title={table.title}
                  history={table.history}
                />
              ))}
            </div>
          ) : null}

          <div className="finance-footer">
            <span>Prepared from the Financial Dashboard print layout</span>
            <span>Hospital Plus</span>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default FinancialReportPrint;
